import { Box, Checkbox, FormControlLabel, FormGroup, Stack, Typography } from '@mui/material';
import React from 'react';

export interface PermissionOption {
    _id: string;
    name: string;
    resource?: string;
    action?: string;
    scope?: string;
}

interface PermissionSelectorProps {
    permissions: PermissionOption[];
    value: string[];
    onChange: (permissionIds: string[]) => void;
    label?: string;
}

const PermissionSelector: React.FC<PermissionSelectorProps> = ({ permissions, value, onChange, label }) => {
    const groupedPermissions = React.useMemo(() => {
        const groups: Record<string, PermissionOption[]> = {};

        permissions.forEach(permission => {
            const resource = permission.resource || 'AUTRES';
            if (!groups[resource]) {
                groups[resource] = [];
            }
            groups[resource].push(permission);
        });

        return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
    }, [permissions]);

    const handlePermissionChange = (permissionId: string, checked: boolean) => {
        if (checked) {
            onChange([...value, permissionId]);
        } else {
            onChange(value.filter(id => id !== permissionId));
        }
    };

    return (
        <Stack spacing={2} sx={{ mt: 1 }}>
            {groupedPermissions.map(([resource, resourcePermissions]) => (
                <Box
                    key={resource}
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        p: 2,
                        backgroundColor: 'background.paper',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            borderColor: 'primary.main',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                        },
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 700,
                            mb: 1.5,
                            textTransform: 'capitalize',
                            color: 'text.primary',
                            fontSize: '0.95rem',
                            letterSpacing: '0.3px',
                        }}
                    >
                        {resource}
                    </Typography>
                    <FormGroup row sx={{ gap: 2.5 }}>
                        {resourcePermissions.map(permission => (
                            <FormControlLabel
                                key={permission._id}
                                control={
                                    <Checkbox
                                        checked={value.includes(permission._id)}
                                        onChange={(event) => handlePermissionChange(permission._id, event.target.checked)}
                                        size="small"
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                        {permission.action || permission.name}
                                    </Typography>
                                }
                                sx={{
                                    m: 0,
                                    '&:hover': { opacity: 0.8 },
                                }}
                            />
                        ))}
                    </FormGroup>
                </Box>
            ))}
        </Stack>
    );
};

export default PermissionSelector;
